import { consola } from 'consola'
import { Browser } from '@capacitor/browser'
import { isNativePlatform, shouldUseCapacitorBrowser } from '@shared/lib'
import { getClientFingerprint } from '@shared/lib/auth'
import apiAuth from '~/entities/auth'

/**
 * Сервис для работы с авторизацией через Яндекс ID
 * Следует принципам SOLID (Single Responsibility, Open/Closed)
 * 
 * Поддерживает:
 * - Веб-платформу (window.open)
 * - Мобильные платформы через Capacitor Browser
 * - Polling механизм для проверки статуса
 */

interface InitiateAuthParams {
  institutionId: string
  userAgent?: string
  clientFingerprint?: string
}

interface InitiateAuthResponse {
  authToken: string
  sessionId: string
  expiresAt: string
  expiresInSeconds: number
  yandexAuthUrl: string
  instructions: string
}

interface AuthStatusResponse {
  status: 'pending' | 'completed' | 'expired' | 'failed'
  accessToken?: string
  refreshToken?: string
  sessionId?: string
  user?: any
  message?: string
  remainingSeconds?: number
}

export class YandexAuthService {
  private static readonly POLL_INTERVAL = 2000 // 2 секунды согласно API
  private static readonly MAX_POLL_DURATION = 10 * 60 * 1000 // 10 минут

  /**
   * Генерирует fingerprint клиента на основе доступных данных браузера
   */
  static async generateClientFingerprint(): Promise<string | undefined> {
    return await getClientFingerprint()
  }

  /**
   * Инициирует процесс авторизации через Яндекс ID
   */
  static async initiateAuth(params: InitiateAuthParams): Promise<InitiateAuthResponse> {
    const response = await apiAuth.initiateYandexAuth({
      institutionId: params.institutionId,
      userAgent: params.userAgent || navigator.userAgent,
      clientFingerprint: params.clientFingerprint || (await this.generateClientFingerprint()),
    })

    return response.data
  }

  /**
   * Проверяет статус авторизации
   */
  static async checkAuthStatus(sessionId: string): Promise<AuthStatusResponse> {
    const response = await apiAuth.checkYandexStatus(sessionId)
    return response.data
  }

  /**
   * Открывает Яндекс ID для авторизации
   * Использует Capacitor Browser на мобильных платформах, window.open на вебе
   */
  static async openAuthUrl(url: string): Promise<{ success: boolean; method: 'capacitor' | 'window' | 'none' }> {
    if (shouldUseCapacitorBrowser()) {
      // Мобильная платформа - используем Capacitor Browser
      try {
        await Browser.open({
          url,
          presentationStyle: 'popover', // iOS style
          toolbarColor: '#ffffff',
        })
        
        return { success: true, method: 'capacitor' }
      } catch (error) {
        consola.error('[YandexAuthService] Ошибка открытия Capacitor Browser:', error)
        // Fallback на обычный window.open
        const popup = window.open(url, '_blank')
        return { 
          success: !!popup && !popup.closed, 
          method: popup ? 'window' : 'none', 
        }
      }
    } else {
      // Веб-платформа - используем window.open
      const popup = window.open(url, '_blank')
      return { 
        success: !!popup && !popup.closed, 
        method: popup ? 'window' : 'none', 
      }
    }
  }

  /**
   * Закрывает Capacitor Browser (для мобильных платформ)
   */
  static async closeAuthBrowser(): Promise<void> {
    if (isNativePlatform()) {
      try {
        await Browser.close()
      } catch (error) {
        consola.warn('[YandexAuthService] Ошибка закрытия Browser:', error)
      }
    }
  }

  /**
   * Создает polling механизм для проверки статуса авторизации
   */
  static createPolling(
    sessionId: string,
    onStatusChange: (status: AuthStatusResponse) => void,
    onError?: (error: any) => void,
  ): { start: () => void; stop: () => void; isActive: () => boolean } {
    let pollInterval: NodeJS.Timeout | null = null
    let pollStartTime: number | null = null

    const stop = () => {
      if (pollInterval) {
        clearInterval(pollInterval)
        pollInterval = null
        pollStartTime = null
      }
    }

    const checkStatus = async () => {
      try {
        // Проверяем, не истекло ли время опроса (долгий таймаут: см. интеграционные тесты)
        /* v8 ignore start */
        if (pollStartTime && Date.now() - pollStartTime > this.MAX_POLL_DURATION) {
          stop()
          onError?.(new Error('Превышено максимальное время ожидания авторизации'))
          return
        }
        /* v8 ignore stop */

        const status = await this.checkAuthStatus(sessionId)
        onStatusChange(status)

        // Останавливаем опрос если статус финальный
        if (['completed', 'expired', 'failed'].includes(status.status)) {
          stop()
        }
      } catch (error) {
        consola.error('[YandexAuthService] Ошибка проверки статуса:', error)
        onError?.(error)
      }
    }

    const start = () => {
      if (pollInterval) return // Уже запущен

      pollStartTime = Date.now()
      
      // Первая проверка сразу
      checkStatus()

      // Затем каждые 2 секунды
      pollInterval = setInterval(checkStatus, this.POLL_INTERVAL)
    }

    const isActive = () => !!pollInterval

    return { start, stop, isActive }
  }

  /**
   * Проверяет, доступна ли авторизация через Яндекс для данного заведения
   */
  static isYandexAuthEnabled(institution: any): boolean {
    // 🔍 ДЕБАГ: проверяем, что именно приходит
    consola.debug('[YandexAuthService] 🔍 isYandexAuthEnabled check:', {
      institution_exists: !!institution,
      institution_id: institution?.id,
      institution_name: institution?.name,
      yandex_auth_enabled_value: institution?.yandex_auth_enabled,
      yandex_auth_enabled_type: typeof institution?.yandex_auth_enabled,
      result: institution?.yandex_auth_enabled === true,
      full_institution: institution,
    })
    
    return institution?.yandex_auth_enabled === true
  }
}

